import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars, faXmark, faSun, faMoon, faHandshake,
  faBriefcase, faLayerGroup, faChartPie, faGear,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';

interface HeaderProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

const AUTH_NAV = [
  { path: '/dashboard', label: '대시보드', icon: faChartPie },
  { path: '/deals', label: '딜 관리', icon: faBriefcase },
  { path: '/templates', label: '템플릿', icon: faLayerGroup },
  { path: '/settings', label: '설정', icon: faGear },
];

const PUBLIC_NAV = [
  { path: '/', label: '홈' },
  { path: '/pricing', label: '요금제' },
];

export function Header({ theme, onToggleTheme }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const navItems = isAuthenticated ? AUTH_NAV : PUBLIC_NAV;
  const isExact = (path: string) => isAuthenticated ? location.pathname.startsWith(path) : location.pathname === path;

  return (
    <>
      <header
        role="banner"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'var(--color-surface-glass)',
          backdropFilter: 'blur(24px) saturate(1.8)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
          borderBottom: '1px solid var(--color-surface-glass-border)',
          padding: 'var(--space-3) var(--space-4)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link
            to={isAuthenticated ? '/dashboard' : '/'}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 800,
              color: 'var(--color-text-primary)', textDecoration: 'none',
            }}
            aria-label="Dealroom Home"
          >
            <FontAwesomeIcon icon={faHandshake} style={{ color: 'var(--color-accent)' }} />
            Dealroom
          </Link>

          <nav aria-label="Main navigation" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <ul className="nav-desktop" style={{ display: 'none', listStyle: 'none', gap: 'var(--space-1)', margin: 0, padding: 0 }}>
              {navItems.map(item => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)', fontWeight: 500, textDecoration: 'none',
                      color: isExact(item.path) ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                      background: isExact(item.path) ? 'var(--color-accent-subtle)' : 'transparent',
                      transition: 'color 0.15s, background 0.15s',
                    }}
                    aria-current={isExact(item.path) ? 'page' : undefined}
                  >
                    {'icon' in item && <FontAwesomeIcon icon={(item as typeof AUTH_NAV[0]).icon} style={{ fontSize: '12px' }} />}
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Auth CTA or avatar */}
            {isAuthenticated && user ? (
              <Link
                to="/settings"
                style={{
                  width: '2rem', height: '2rem', borderRadius: 'var(--radius-full)', flexShrink: 0,
                  background: 'var(--color-accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, color: 'var(--color-accent)', fontSize: 'var(--text-xs)', textDecoration: 'none',
                  border: '2px solid var(--color-surface-glass-border)',
                }}
                aria-label="Profile settings"
              >
                {(user.name || user.email)[0].toUpperCase()}
              </Link>
            ) : (
              <Link
                to="/login"
                className="nav-desktop"
                style={{
                  display: 'none', alignItems: 'center', gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)', fontWeight: 600, textDecoration: 'none',
                  color: 'var(--color-text-on-accent)',
                  background: 'var(--color-accent)',
                  transition: 'opacity 0.15s',
                }}
              >
                로그인 <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '11px' }} />
              </Link>
            )}

            <button
              onClick={onToggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '2.25rem', height: '2.25rem', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-subtle)', background: 'var(--color-surface-glass)',
                color: 'var(--color-text-secondary)', cursor: 'pointer', transition: 'color 0.15s',
              }}
            >
              <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
            </button>

            <button
              className="hamburger-btn"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '2.25rem', height: '2.25rem', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-subtle)', background: 'var(--color-surface-glass)',
                color: 'var(--color-text-secondary)', cursor: 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          </nav>
        </div>
      </header>

      {/* Fullscreen Mobile Overlay */}
      {mobileOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'var(--color-bg-primary)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-8)',
            animation: 'fadeIn 0.3s ease-out',
          }}
        >
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            style={{
              position: 'absolute', top: 'var(--space-4)', right: 'var(--space-4)',
              width: '2.5rem', height: '2.5rem', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)', background: 'var(--color-surface)',
              color: 'var(--color-text-primary)', fontSize: 'var(--text-xl)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <nav aria-label="Mobile navigation">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'center' }}>
              {navItems.map((item, i) => (
                <li key={item.path} style={{ marginBottom: 'var(--space-6)' }}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      fontFamily: 'var(--font-display)', fontSize: 'var(--text-2xl)', fontWeight: 700,
                      color: isExact(item.path) ? 'var(--color-accent)' : 'var(--color-text-primary)',
                      textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-3)',
                      opacity: 0, animation: `slideUp 0.4s ease-out ${i * 0.08}s forwards`,
                    }}
                    aria-current={isExact(item.path) ? 'page' : undefined}
                  >
                    {'icon' in item && <FontAwesomeIcon icon={(item as typeof AUTH_NAV[0]).icon} />}
                    {item.label}
                  </Link>
                </li>
              ))}
              {!isAuthenticated && (
                <li>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      fontFamily: 'var(--font-display)', fontSize: 'var(--text-2xl)', fontWeight: 700,
                      color: 'var(--color-accent)', textDecoration: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-3)',
                      opacity: 0, animation: `slideUp 0.4s ease-out ${navItems.length * 0.08}s forwards`,
                    }}
                  >
                    로그인 <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .nav-desktop { display: none !important; }
        .hamburger-btn { display: flex !important; }
        @media (min-width: 768px) { .nav-desktop { display: flex !important; } .hamburger-btn { display: none !important; } }
      `}</style>
    </>
  );
}
